import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { useAgentStore } from "@/stores/agent-store";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { MatchWorkflow } from "@/components/match/MatchWorkflow";
import { MatchDraftGuard } from "@/components/match/MatchDraftGuard";
import { useMatchEditor } from "@/components/match/use-match-editor";

export function MatchDetailPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();

  if (!matchId) {
    return (
      <MatchEditorMessage>
        <FormattedMessage id="match.detail.invalidUrl" defaultMessage="Invalid match URL." />
      </MatchEditorMessage>
    );
  }

  return <MatchEditor matchId={matchId} onAfterDiscard={() => navigate("/match")} />;
}

function MatchEditor({
  matchId,
  onAfterDiscard,
}: {
  matchId: string;
  onAfterDiscard: () => void;
}) {
  const intl = useIntl();
  const editor = useMatchEditor(matchId, { onAfterDiscard });

  // Publish the current workflow step as the assistant match subsection (result step → results,
  // otherwise setup) so the Assistant is offered the right tool set (`match/setup` vs
  // `match/results`). Cleared on unmount so the bare `match` list surface stays subsection-less.
  const setMatchSubsection = useAgentStore((s) => s.setMatchSubsection);
  useEffect(() => {
    setMatchSubsection(editor.stepId === "result" ? "results" : "setup");
    return () => setMatchSubsection(null);
  }, [editor.stepId, setMatchSubsection]);

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the open match + which workflow step the user is
  // on. Candidate count only (candidate records are other people's data). Called before the early
  // returns below so hook order stays stable.
  useAssistantContext(
    !editor.loading && editor.data
      ? [
          {
            description: "the match the user is editing",
            value:
              `"${editor.data.matchTitle || "untitled match"}"` +
              (editor.data.projectName ? ` for project "${editor.data.projectName}"` : "") +
              (editor.data.projectShortDescription
                ? ` — ${editor.data.projectShortDescription.slice(0, 200)}`
                : "") +
              `; workflow step: ${editor.stepId}` +
              `; ${editor.candidates.length} candidates, ${editor.results.length} saved results` +
              `; the user's role: ${editor.myRole}`,
          },
        ]
      : null,
  );

  // Guided tour (/tour): summary-only — the workflow steps render inside
  // MatchWorkflow, which offers no stable anchor in this file, so there are no
  // stops. Called before the early returns below so hook order stays stable.
  useAssistantTour(
    !editor.loading && editor.data
      ? {
          label: intl.formatMessage({
            id: "matchDetail.tour.label",
            defaultMessage: "Match Detail",
          }),
          summary: intl.formatMessage(
            {
              id: "matchDetail.tour.summary",
              defaultMessage:
                "You are editing the match \"{name}\", currently on the \"{step}\" workflow step with {candidates} candidates and {results} saved results. Work through the steps from requirements to the solved team.",
            },
            {
              name:
                editor.data.matchTitle ||
                intl.formatMessage({ id: "matchDetail.tour.untitled", defaultMessage: "untitled match" }),
              step: editor.stepId,
              candidates: editor.candidates.length,
              results: editor.results.length,
            },
          ),
          stops: [],
        }
      : null,
  );

  if (editor.loading) {
    return (
      <MatchEditorMessage>
        <FormattedMessage id="match.detail.loading" defaultMessage="Loading…" />
      </MatchEditorMessage>
    );
  }
  if (editor.notFound) {
    return (
      <MatchEditorMessage>
        <FormattedMessage id="match.detail.notFound" defaultMessage="Match not found." />
      </MatchEditorMessage>
    );
  }
  if (editor.error && !editor.data) {
    return (
      <MatchEditorMessage>
        <FormattedMessage
          id="match.detail.error"
          defaultMessage="Error: {message}"
          values={{ message: userErrorMessage(intl, editor.error) }}
        />
      </MatchEditorMessage>
    );
  }

  return (
    <>
      <MatchDraftGuard
        hasUnsavedTransient={editor.hasUnsavedTransient}
        onDiscard={editor.clearSolverResult}
      />
      <MatchWorkflow editor={editor} />
    </>
  );
}

function MatchEditorMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="bg-page box-flat p-6 text-smycles-body text-ink-muted">{children}</div>
    </div>
  );
}
